import { EditOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button as AntButton, Space, Table, Tooltip } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import GenericTableContainer from "~/components/GenericTableContainer";
import { dateTimeFormat, roles } from "~/config";
import useAuthorization from "~/hooks/useAuthorization";
import useColumnSearch from "~/hooks/useColumnSearch";
import { getLocationList } from "~/services/location.service";
import { getTableParams } from "~/utils/utils";

const LocationMaster = () => {
  const navigate = useNavigate();
  const { getColumnSearchProps, setSearchText, setSearchedColumn } =
    useColumnSearch();
  const { userHasRequiredRole } = useAuthorization([roles.ADMIN]);

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 25,
    },
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);

    mutate(
      getTableParams({
        pagination: tableParams.pagination,
        filters: selectedKeys[0],
      })
    );
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };
  //

  const columns = [
    {
      title: "Location Name",
      dataIndex: "Loc_Name",
      key: "Loc_Name",
      // render: (text) => <a>{text}</a>,
      fixed: "left",
      width: 300,
      ...getColumnSearchProps("Loc_Name", handleSearch, "Location Name"),
    },
    {
      title: "Location Type",
      dataIndex: "Loc_Type",
      key: "Loc_Type",
      responsive: ["lg", "md"],
    },
    {
      title: "Total Location Area (Hect.)",
      dataIndex: "Loc_Total_Area",
      key: "Loc_Total_Area",
      responsive: ["lg", "md"],
    },
    {
      title: "Type of Land",
      dataIndex: "Type_of_land",
      key: "Type_of_land",
      responsive: ["lg", "md"],
    },
    {
      title: "Total Plotting Count",
      dataIndex: "Total_Ploting_Count",
      key: "Total_Ploting_Count",
      responsive: ["lg", "md"],
    },
    {
      title: "Created At",
      dataIndex: "Created_Dt",
      key: "Created_Dt",
      responsive: ["lg", "md"],
      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Created By",
      dataIndex: "created_by.Login_Name",
      key: "created_by.Login_Name",
      responsive: ["lg", "md"],
    },
    {
      title: "Modified At",
      dataIndex: "Modified_Dt",
      key: "Modified_Dt",
      responsive: ["lg", "md"],

      render: (record) =>
        record === null ? "" : moment(record).format(dateTimeFormat),
    },
    {
      title: "Modified By",
      dataIndex: "modified_by.Login_Name",
      key: "modified_by.Login_Name",
      responsive: ["lg", "md"],
    },
    ...(userHasRequiredRole ? [{
      title: "Action",
      key: "action",
      fixed: "right",
      width: 100,
      render: (rowData, record) => (
        <Space size="middle">
          <Tooltip title="Edit">
            <AntButton
              type="primary"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  `/location-master/update/${rowData.Loc_Name.trim().replace(
                    /\s+/g,
                    "-"
                  )}`,
                  {
                    state: rowData,
                  }
                )
              }
            />
          </Tooltip>
        </Space>
      ),
    }]: []),
  ];

  const {
    data: locations,
    isLoading,
    mutate,
  } = useMutation({
    mutationKey: ["locations"],
    mutationFn: (params) => getLocationList(params),
    onSuccess: (data) => {
      // Modify the data response here
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: data.count,
          // 200 is mock data, you should read it from server
          // total: data.totalCount,
        },
      });
      // console.log("ON SUCCESS", data);
    },
  });

  const handleTableChange = (pagination, filters, sorter) => {
    mutate(getTableParams({ pagination }));

    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  useEffect(() => {
    mutate(getTableParams(tableParams));
  }, []);

  return (
    <GenericTableContainer
      cardHeaderTitle="Location Master"
      navigateTo="/location-master/create"
      cardButtonText="Add Location"
    >
      <Table
        loading={isLoading}
        scroll={{ x: 2000, y: 350 }}
        columns={columns}
        dataSource={locations?.data}
        pagination={{
          ...tableParams.pagination,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />
    </GenericTableContainer>
  );
};

export default LocationMaster;
